import React from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Typography
} from '@material-ui/core';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';
import { APP_PATH_PRODUCTS, APP_PATH_SIGNUP } from '../../config';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(12),
    background: theme.palette.background.default
  },
  heroSection: {
    textAlign: 'center',
    marginBottom: theme.spacing(8)
  },
  heroEyebrow: {
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1)
  },
  heroTitle: {
    fontWeight: 700,
    fontSize: '2.8rem',
    lineHeight: 1.2,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem'
    }
  },
  heroSubtitle: {
    fontSize: '1.1rem',
    color: theme.palette.text.secondary,
    maxWidth: '780px',
    margin: '0 auto',
    marginBottom: theme.spacing(3),
    lineHeight: 1.6
  },
  heroCta: {
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1.2, 4)
  },
  section: {
    marginBottom: theme.spacing(8)
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '2rem',
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem'
    }
  },
  featureGrid: {
    marginTop: theme.spacing(3)
  },
  featureCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    height: '100%'
  },
  featureTitle: {
    fontWeight: 600,
    fontSize: '1.05rem',
    color: theme.palette.text.primary
  },
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'grid',
    gap: theme.spacing(1)
  },
  listItem: {
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
    lineHeight: 1.6,
    paddingLeft: theme.spacing(2),
    position: 'relative',
    '&::before': {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      position: 'absolute',
      left: 0,
      top: '0.6em'
    }
  },
  availabilityCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper
  },
  mutedLine: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    marginTop: theme.spacing(1)
  },
  sectionDivider: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6)
  },
  finalCta: {
    textAlign: 'center',
    marginTop: theme.spacing(6)
  },
  finalTitle: {
    fontWeight: 700,
    fontSize: '2.1rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.6rem'
    }
  },
  finalCtaRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  finalPrimaryCta: {
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1.2, 4)
  },
  finalSecondaryCta: {
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1.2, 4)
  }
}));

const AiComingSoon = () => {
  const classes = useStyles();

  usePageTitle('AI tools for recruiting - Remote Job');

  return (
    <div className={classes.root}>
      <Helmet>
        <title>AI tools for recruiting - Remote Job</title>
        <meta
          name="description"
          content="Explore upcoming AI-powered features designed to help recruiters review candidates faster inside Remote Job."
        />
      </Helmet>
      <Container maxWidth="lg">
        <Box className={classes.heroSection}>
          <Typography className={classes.heroEyebrow} component="p">
            Coming Soon
          </Typography>
          <Typography className={classes.heroTitle} variant="h1" component="h1">
            AI tools to help you review candidates faster
          </Typography>
          <Typography
            className={classes.heroSubtitle}
            variant="h5"
            component="p">
            We’re building practical AI features designed to reduce manual work
            during hiring — without changing how you hire or adding complexity.
          </Typography>
          <Button
            component={RouterLink}
            to={APP_PATH_SIGNUP}
            variant="contained"
            color="primary"
            className={classes.heroCta}>
            Post a Job
          </Button>
        </Box>

        <Box className={classes.section}>
          <Typography
            className={classes.sectionTitle}
            variant="h2"
            component="h2">
            What’s coming next
          </Typography>
          <Grid container spacing={3} className={classes.featureGrid}>
            <Grid item xs={12} md={6}>
              <Box className={classes.featureCard}>
                <Typography className={classes.featureTitle} component="h3">
                  Resume parsing & structured data
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.featureCard}>
                <Typography className={classes.featureTitle} component="h3">
                  Recruiter-friendly candidate summaries
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.featureCard}>
                <Typography className={classes.featureTitle} component="h3">
                  Fit insights against job requirements
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.featureCard}>
                <Typography className={classes.featureTitle} component="h3">
                  Personalized outreach drafts
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider className={classes.sectionDivider} />

        <Box className={classes.section}>
          <Typography
            className={classes.sectionTitle}
            variant="h2"
            component="h2">
            Built into your existing hiring process
          </Typography>
          <Box component="ul" className={classes.list}>
            <Box component="li" className={classes.listItem}>
              No new tools to learn
            </Box>
            <Box component="li" className={classes.listItem}>
              No setup required
            </Box>
            <Box component="li" className={classes.listItem}>
              Works inside candidate cards
            </Box>
            <Box component="li" className={classes.listItem}>
              Supports decision-making, not automation
            </Box>
          </Box>
        </Box>

        <Divider className={classes.sectionDivider} />

        <Box className={classes.section}>
          <Typography
            className={classes.sectionTitle}
            variant="h2"
            component="h2">
            What to expect (and what not to expect)
          </Typography>
          <Box component="ul" className={classes.list}>
            <Box component="li" className={classes.listItem}>
              Not full automation
            </Box>
            <Box component="li" className={classes.listItem}>
              Not a recruiter replacement
            </Box>
            <Box component="li" className={classes.listItem}>
              Not black-box decisions
            </Box>
            <Box component="li" className={classes.listItem}>
              Not required to use the platform
            </Box>
          </Box>
        </Box>

        <Divider className={classes.sectionDivider} />

        <Box className={classes.section}>
          <Typography
            className={classes.sectionTitle}
            variant="h2"
            component="h2">
            Availability
          </Typography>
          <Box className={classes.availabilityCard}>
            <Typography variant="body1" component="p" color="textSecondary">
              We’re actively building and testing these features. They’ll be
              introduced gradually and made available inside the platform when
              ready.
            </Typography>
            <Typography className={classes.mutedLine} component="p">
              No action required. No setup needed.
            </Typography>
          </Box>
        </Box>

        <Divider className={classes.sectionDivider} />

        <Box className={classes.finalCta}>
          <Typography
            className={classes.finalTitle}
            variant="h2"
            component="h2">
            Hire confidently today. Move faster tomorrow.
          </Typography>
          <Box className={classes.finalCtaRow}>
            <Button
              component={RouterLink}
              to={APP_PATH_SIGNUP}
              variant="contained"
              color="primary"
              className={classes.finalPrimaryCta}>
              Post a Job
            </Button>
            <Button
              component={RouterLink}
              to={APP_PATH_PRODUCTS}
              variant="outlined"
              className={classes.finalSecondaryCta}>
              Explore Platform Features
            </Button>
          </Box>
        </Box>
      </Container>
    </div>
  );
};

export default AiComingSoon;
